// src/store/lead/leadSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  addExcelDataAction,
  fetchLeadsAction,
  fetchCampaignsAction,
  deleteCampaignsAction,
  startCampaignAction,
  stopCampaignAction,
  searchLeadsAction,
  markMessageReadAction,
} from "./leadThunk";
import { changeWorkerAction } from "../worker/workerThunk";
import { set } from "zod";

const initialState = {
  leads: [],
  campaigns: [],
  isLoading: false,
  fetchLeadsLoading: false,
  error: null,
  uploadedFileNames: [],
  selectedCampaign: null,
  totalRecords: 0,
  totalPages: 0,
  currentPage: 1,
  isSearchResult: false,
  searchKeyword: "",
};

const leadSlice = createSlice({
  name: "leads",
  initialState,
  reducers: {
    addUploadedFileName: (state, action) => {
      state.uploadedFileNames.push(action.payload);
    },
    removeUploadedFileName: (state, action) => {
      state.uploadedFileNames = state.uploadedFileNames.filter(
        (fileName, index) => index !== action.payload
      );
    },
    setSelectedCampaign: (state, action) => {
      state.selectedCampaign = action.payload;
    },
    addCampaign: (state, action) => {
      state.campaigns.push(action.payload);
    },
    removeCompaign: (state, action) => {
      console.log("remove compagin", action.payload);
      state.campaigns = state.campaigns.filter(
        (campaign) => campaign.value !== action.payload
      );
    },
    startCompaign: (state, action) => {
      state.campaigns = state.campaigns.map((campaign) =>
        campaign.id === action.payload.id // Ensure matching key
          ? { ...campaign, status: action.payload.status }
          : campaign
      );
    },
    stopCompaign: (state, action) => {
      console.log("Stop campaign action received", action.payload);

      state.campaigns = state.campaigns.map((campaign) =>
        campaign.id === action.payload.campaign_id // Ensure matching key
          ? { ...campaign, status: action.payload.status }
          : campaign
      );

      console.log("Updated campaigns list:", state.campaigns);
    },

    reset: (state) => initialState,
    resetLead: (state) => {
      state.leads = [];
    },
    clearSearch: (state) => {
      state.isSearchResult = false;
      state.searchKeyword = "";
    },
    updateWorker: (state, action) => {
      const { LeadAssignmentId, Worker, WorkerId } = action.payload;
      state.leads = state.leads.map((lead) =>
        lead.LeadAssignmentId === LeadAssignmentId
          ? { ...lead, Worker, WorkerId }
          : lead
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addExcelDataAction.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addExcelDataAction.fulfilled, (state, action) => {
        console.log(action.payload);
        if (action.payload.details) {
          const campaignId = action.payload.details.CompaignId;
          // Set this as the selected campaign
          state.selectedCampaign = {
            id: campaignId,
            value: campaignId,
            label: action.payload.details.campaignName || 'New Campaign'
          };
          // Reset leads for the new campaign
          state.leads = [];
          state.currentPage = 1;
          state.totalPages = 0;
          state.totalRecords = 0;
        }
        state.isLoading = false;
      })
      .addCase(addExcelDataAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchLeadsAction.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeadsAction.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leads = action.payload.leads;
        state.totalRecords = action.payload.total_records;
        state.totalPages = action.payload.total_pages;
        state.currentPage = action.payload.current_page;
      })
      .addCase(fetchLeadsAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchCampaignsAction.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCampaignsAction.fulfilled, (state, action) => {
        state.campaigns = action.payload;

        state.isLoading = false;
      })
      .addCase(fetchCampaignsAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteCampaignsAction.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCampaignsAction.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(deleteCampaignsAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        console.log(action.payload, "delete");
      })
      .addCase(startCampaignAction.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(startCampaignAction.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(startCampaignAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(stopCampaignAction.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(stopCampaignAction.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(stopCampaignAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(changeWorkerAction.fulfilled, (state, action) => {
        const { LeadAssignmentId, Worker, WorkerId } = action.payload;
        state.leads = state.leads.map((lead) =>
          lead.LeadAssignmentId === LeadAssignmentId
            ? { ...lead, Worker, WorkerId }
            : lead
        );
        state.isLoading = false;
      })
      .addCase(searchLeadsAction.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchLeadsAction.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leads = action.payload.leads;
        state.totalRecords = action.payload.total_records;
        state.totalPages = action.payload.total_pages;
        state.currentPage = action.payload.current_page;
        state.isSearchResult = true;
        state.searchKeyword = action.payload.search_keyword;
      })
      .addCase(searchLeadsAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(markMessageReadAction.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(markMessageReadAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  addUploadedFileName,
  removeUploadedFileName,
  reset,
  updateWorker,
  setSelectedCampaign,
  addCampaign,
  removeCompaign,
  startCompaign,
  stopCompaign,
  resetLead,
  clearSearch,
} = leadSlice.actions;
export default leadSlice.reducer;
